<template>
  <div class="modal fade show d-block" style="background-color: rgba(0, 0, 0, 0.5);" @click.self="close">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isExportMode ? ($t("Send export by email") || "Send export by email") : ($t("Send document by email") || "Send document by email") }}</h5>
          <button type="button" class="btn-close" @click="close" aria-label="Close"></button>
        </div>

        <div class="modal-body">
        <!-- Loading state -->
        <ContentSkeleton v-if="loading" variant="list" :rows="5" padded />

        <!-- Recipients selection -->
        <div v-else>
          <!-- System Users -->
          <div class="mb-4">
            <h6 class="mb-3">{{ $t("System Users") || "System Users" }}</h6>
            <div class="border rounded p-2" style="max-height: 200px; overflow-y: auto;">
              <div
                v-for="user in systemUsers"
                :key="`user-${user._id}`"
                class="form-check py-2"
              >
                <input
                  type="checkbox"
                  :value="user.email"
                  v-model="selectedRecipients"
                  :disabled="!user.email"
                  class="form-check-input"
                  :id="`user-${user._id}`"
                />
                <label class="form-check-label d-flex justify-content-between w-100" :for="`user-${user._id}`">
                  <span>{{ user.name || user.email }}</span>
                  <small class="text-muted ms-2">{{ user.email }}</small>
                </label>
              </div>
            </div>
          </div>

          <!-- Document Emails -->
          <div v-if="documentEmails.length > 0" class="mb-4">
            <h6 class="mb-3">{{ $t("Document Emails") || "Document Emails" }}</h6>
            <div class="border rounded p-2" style="max-height: 200px; overflow-y: auto;">
              <div
                v-for="(email, index) in documentEmails"
                :key="`doc-email-${index}`"
                class="form-check py-2"
              >
                <input
                  type="checkbox"
                  :value="email.email"
                  v-model="selectedRecipients"
                  class="form-check-input"
                  :id="`doc-email-${index}`"
                />
                <label class="form-check-label d-flex justify-content-between w-100" :for="`doc-email-${index}`">
                  <span>{{ email.label || email.email }}</span>
                  <small class="text-muted ms-2">{{ email.email }}</small>
                </label>
              </div>
            </div>
          </div>

          <!-- Manual Email Input -->
          <div class="mb-4">
            <h6 class="mb-3">{{ $t("Manual Email") || "Manual Email" }}</h6>
            <div class="input-group mb-2">
              <input
                type="email"
                v-model="manualEmail"
                :placeholder="$t('Enter email address') || 'Enter email address'"
                @keyup.enter="addManualEmail"
                class="form-control"
              />
              <button
                @click="addManualEmail"
                class="btn btn-primary"
                type="button"
                :disabled="!isValidEmail(manualEmail)"
              >
                {{ $t("Add") || "Add" }}
              </button>
            </div>
            <div v-if="manualEmails.length > 0" class="d-flex flex-wrap gap-2">
              <span
                v-for="(email, index) in manualEmails"
                :key="`manual-${index}`"
                class="badge bg-secondary d-flex align-items-center gap-1"
              >
                {{ email }}
                <button
                  @click="removeManualEmail(index)"
                  class="btn-close btn-close-white"
                  style="font-size: 0.6rem;"
                  type="button"
                  aria-label="Remove"
                ></button>
              </span>
            </div>
          </div>

          <!-- Selected Recipients Summary -->
          <div v-if="allSelectedRecipients.length > 0" class="mb-4 pt-3 border-top">
            <h6 class="mb-3">
              {{ $t("Selected Recipients") || "Selected Recipients" }} ({{ allSelectedRecipients.length }})
            </h6>
            <div class="d-flex flex-wrap gap-2">
              <span
                v-for="(email, index) in allSelectedRecipients"
                :key="`selected-${index}`"
                class="badge bg-primary"
              >
                {{ email }}
              </span>
            </div>
          </div>

          <!-- Email Subject -->
          <div class="mb-3">
            <label class="form-label">
              {{ $t("Email Subject") || "Email Subject" }}
            </label>
            <input
              type="text"
              v-model="emailSubject"
              class="form-control"
              :placeholder="$t('Enter email subject') || 'Enter email subject'"
            />
          </div>

          <!-- Email Body -->
          <div class="mb-3">
            <label class="form-label">
              {{ $t("Email Body") || "Email Body" }}
            </label>
            <textarea
              v-model="emailBody"
              :placeholder="$t('Enter email body') || 'Enter email body'"
              class="form-control"
              rows="6"
            ></textarea>
          </div>

          <!-- Excel attachment (export mode): show filename only -->
          <div v-if="isExportMode && excelFileName" class="mb-4 pt-3 border-top">
            <h6 class="mb-2">{{ $t("Attachment") || "Attachment" }}</h6>
            <span class="text-muted small"><i class="fas fa-file-excel me-1"></i>{{ excelFileName }}</span>
          </div>

          <!-- PDF attachment: download the PDF that will be sent (language from the selector) -->
          <div v-else-if="(effectivePdfBase64 && effectivePdfFileName) || regeneratingPdf" class="mb-4 pt-3 border-top">
            <h6 class="mb-2">{{ $t("PDF attachment") || "PDF attachment" }}</h6>
            <div v-if="pdfBase64 || emailPdfBase64" class="mb-2">
              <label class="form-label small mb-1">{{ $t("emails.pdfLanguage") || "PDF language" }}</label>
              <select
                v-model="pdfOnlyLocale"
                class="form-select form-select-sm"
                style="max-width: 220px;"
                :disabled="regeneratingPdf"
                @change="onPdfOnlyLocaleChange"
              >
                <option
                  v-for="loc in availablePdfLocales"
                  :key="loc"
                  :value="loc"
                >
                  {{ localeLabel(loc) }}
                </option>
              </select>
            </div>
            <template v-if="regeneratingPdf">
              <span class="text-muted small d-flex align-items-center gap-1">
                <span class="spinner-border spinner-border-sm" role="status"></span>
                {{ $t("emails.regeneratingPdf") || "Regenerating PDF in selected language..." }}
              </span>
            </template>
            <template v-else>
              <a
                :href="pdfDownloadUrl || pdfDownloadDataUri"
                :download="effectivePdfFileName"
                class="btn btn-outline-secondary btn-sm"
                rel="noopener"
              >
                <i class="material-icons align-middle me-1" style="font-size: 1rem;">download</i>
                {{ $t("Download PDF") || "Download PDF" }} ({{ effectivePdfFileName }})
              </a>
              <small v-if="emailPdfBase64" class="d-block text-muted mt-1">
                {{ $t("emails.pdfInSelectedLanguageHint") || "PDF is in the selected language." }}
              </small>
            </template>
          </div>

        </div>
      </div>

      <div class="modal-footer">
        <button @click="close" class="btn btn-secondary">
          {{ $t("Cancel") || "Cancel" }}
        </button>
        <button
          @click="sendEmail"
          class="btn btn-primary"
          :disabled="allSelectedRecipients.length === 0 || sending"
        >
          <span v-if="sending" class="d-flex align-items-center">
            <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ $t("Sending...") || "Sending..." }}
          </span>
          <span v-else-if="sendMode === 'mail_client'" class="d-flex align-items-center">
            <i class="material-icons me-1">drafts</i>
            {{ $t("emails.smartEmailOpenInMailClient") || "Open in my email" }}
          </span>
          <span v-else class="d-flex align-items-center">
            <i class="material-icons me-1">send</i>
            {{ $t("Send") || "Send" }}
          </span>
        </button>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import html2pdf from "html2pdf.js";

export default {
  name: "EmailTemplateSender",
  components: { ContentSkeleton },
  props: {
    documentType: {
      type: String,
      required: true,
    },
    documentId: {
      type: String,
      default: null,
    },
    modelName: {
      type: String,
      required: true,
    },
    /** Pre-generated PDF (base64). When set, this PDF is used for send and user can download it. */
    pdfBase64: {
      type: String,
      default: null,
    },
    /** Filename for the pre-generated PDF. */
    pdfFileName: {
      type: String,
      default: null,
    },
    /** View ID (required when documentType is "display" for gotrading viewhtml). */
    viewId: {
      type: String,
      default: null,
    },
    /** For documentType "export": Excel file blob to attach (list/export send by email). */
    excelBlob: {
      type: Blob,
      default: null,
    },
    /** For documentType "export": Excel file name. */
    excelFileName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      sending: false,
      systemUsers: [],
      documentEmails: [],
      selectedRecipients: [],
      manualEmail: "",
      manualEmails: [],
      emailSubject: "",
      emailBody: "",
      documentName: "",
      pdfDownloadUrl: null,
      /** PDF regenerated in selected language (overrides props when set) */
      emailPdfBase64: null,
      emailPdfFileName: null,
      regeneratingPdf: false,
      /** When no template: locale used only for the PDF attachment (user choice) */
      pdfOnlyLocale: "",
      /** Forma de envío elegida en Características: 'server' (lo manda el sistema) o 'mail_client' (borrador .eml). */
      sendMode: "server",
    };
  },
  computed: {
    allSelectedRecipients() {
      return [
        ...this.selectedRecipients.filter((email) => email),
        ...this.manualEmails,
      ].filter((email, index, self) => self.indexOf(email) === index);
    },
    /** Use regenerated PDF in selected language when available, else props */
    effectivePdfBase64() {
      return this.emailPdfBase64 != null ? this.emailPdfBase64 : this.pdfBase64;
    },
    effectivePdfFileName() {
      return this.emailPdfFileName != null ? this.emailPdfFileName : this.pdfFileName;
    },
    /** Data URI for download link: works with any PDF source (server or client), same as DocumentTemplate */
    pdfDownloadDataUri() {
      if (!this.effectivePdfBase64 || !this.effectivePdfFileName) return null;
      return `data:application/pdf;base64,${this.effectivePdfBase64}`;
    },
    /** Locale codes offered for "PDF language" (same as localeLabel keys) */
    availablePdfLocales() {
      return ["en", "es", "fr", "uk", "pt"];
    },
    /** True when sending list/export Excel (different attachment and endpoint). */
    isExportMode() {
      return this.documentType === "export" && this.excelBlob && this.excelFileName;
    },
  },
  watch: {
    pdfBase64() { this.updatePdfDownloadUrl(); },
    pdfFileName() { this.updatePdfDownloadUrl(); },
    effectivePdfBase64() { this.updatePdfDownloadUrl(); },
    effectivePdfFileName() { this.updatePdfDownloadUrl(); },
  },
  beforeDestroy() {
    if (this.pdfDownloadUrl) {
      URL.revokeObjectURL(this.pdfDownloadUrl);
    }
  },
  async mounted() {
    const pageLocale = (this.$i18n?.locale || "en").toString().toLowerCase();
    if (!this.pdfOnlyLocale) {
      this.pdfOnlyLocale = this.availablePdfLocales.includes(pageLocale) ? pageLocale : "en";
    }
    this.$nextTick(() => this.updatePdfDownloadUrl());
    this.loadSendMode();
    await this.loadRecipients();
    this.$nextTick(() => this.updatePdfDownloadUrl());
  },
  methods: {
    updatePdfDownloadUrl() {
      if (this.pdfDownloadUrl) {
        URL.revokeObjectURL(this.pdfDownloadUrl);
        this.pdfDownloadUrl = null;
      }
      const base64 = this.effectivePdfBase64;
      const fileName = this.effectivePdfFileName;
      if (base64 && fileName) {
        try {
          const binary = atob(base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          const blob = new Blob([bytes], { type: "application/pdf" });
          this.pdfDownloadUrl = URL.createObjectURL(blob);
        } catch (e) {
          this.pdfDownloadUrl = null;
        }
      }
    },
    /** Flatten nested locale messages to { "key.nested": "value" } for backend {t:key} replacement */
    flattenLocaleMessages(locale) {
      const messages = this.$i18n?.getLocaleMessage?.(locale) || {};
      const flat = {};
      function visit(obj, prefix) {
        if (obj == null || typeof obj !== "object") return;
        Object.keys(obj).forEach((k) => {
          const v = obj[k];
          const key = prefix ? `${prefix}.${k}` : k;
          if (v != null && typeof v === "object" && !Array.isArray(v)) visit(v, key);
          else flat[key] = typeof v === "string" ? v : String(v ?? "");
        });
      }
      visit(messages, "");
      return flat;
    },
    /** Fetch HTML for the document in the given locale and generate PDF from it (without changing global locale). */
    async regeneratePdfForLocale() {
      const locale = this.pdfOnlyLocale || this.$i18n?.locale || "en";
      this.regeneratingPdf = true;
      this.emailPdfBase64 = null;
      this.emailPdfFileName = null;
      try {
        const payload = {
          documentType: this.documentType,
          documentId: this.documentId,
          modelName: this.modelName,
          locale,
        };
        if (this.documentType === "display" && this.viewId) {
          payload.viewId = this.viewId;
          payload.translations = this.flattenLocaleMessages(locale);
        }
        const res = await wencesApi.post("/api/emails/render-document-html", payload);
        if (!res.data?.success || !res.data?.html) {
          throw new Error(res.data?.message || "Failed to get document HTML");
        }
        const html = res.data.html;
        const iframe = document.createElement("iframe");
        iframe.setAttribute("title", "pdf-regenerate");
        iframe.style.cssText = "position:absolute;left:-9999px;width:210mm;height:297mm;border:0;";
        document.body.appendChild(iframe);
        const doc = iframe.contentDocument;
        doc.open();
        doc.write(html);
        doc.close();
        await new Promise((r) => setTimeout(r, 150));
        const opt = {
          margin: 0,
          image: { type: "jpeg", quality: 0.92 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: "#ffffff",
            letterRendering: true,
            logging: false,
          },
          jsPDF: { unit: "cm", format: "a4", orientation: "portrait" },
        };
        const pdfBlob = await html2pdf().set(opt).from(iframe.contentDocument.body).outputPdf("blob");
        document.body.removeChild(iframe);
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result;
            resolve(dataUrl.replace(/^data:application\/pdf;base64,/, ""));
          };
          reader.onerror = reject;
          reader.readAsDataURL(pdfBlob);
        });
        const name = this.documentName || this.documentId;
        const clean = String(name).replace(/[^a-zA-Z0-9_-]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "") || "document";
        this.emailPdfBase64 = base64;
        this.emailPdfFileName = `${this.documentType}-${clean}.pdf`;
      } catch (e) {
        console.error("Regenerate PDF for locale failed:", e);
        this.$root.$emit(
          "show-error",
          this.$t("emails.pdfRegenerateError") || e.response?.data?.message || "Could not generate PDF in selected language."
        );
      } finally {
        this.regeneratingPdf = false;
      }
    },
    async loadRecipients() {
      try {
        this.loading = true;
        const params = {
          documentType: this.documentType,
          modelName: this.modelName,
        };
        if (!this.isExportMode && this.documentId) {
          params.documentId = this.documentId;
        }
        if (this.documentType === "display" && this.viewId) {
          params.viewId = this.viewId;
        }
        const response = await wencesApi.get(
          `/api/emails/template-recipients`,
          { params }
        );

        if (response.data.success) {
          this.systemUsers = response.data.users || [];
          this.documentEmails = response.data.documentEmails || [];
          this.documentName = response.data.documentName || (this.isExportMode ? this.modelName : this.documentId);
          this.setDefaultEmailContent();
        }
      } catch (error) {
        console.error("Error loading recipients:", error);
        this.$root.$emit(
          "show-error",
          error.response?.data?.message ||
            "Error loading recipients. Please try again."
        );
      } finally {
        this.loading = false;
      }
    },

    isValidEmail(email) {
      if (!email) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    setDefaultEmailContent() {
      const documentTypeLabel = this.documentType.charAt(0).toUpperCase() + this.documentType.slice(1).replace(/-/g, " ");
      const name = this.documentName || this.documentId || this.modelName;
      this.emailSubject = `${documentTypeLabel}: ${name}`;
      this.emailBody = this.isExportMode
        ? (this.$t("Please find attached the export.") || "Please find attached the export.")
        : `Please find attached the ${this.documentType} document.\n\nDocument: ${name}\n\nBest regards`;
    },

    localeLabel(code) {
      const labels = { en: "English", es: "Español", fr: "Français", uk: "Українська", pt: "Português" };
      return labels[code] || code;
    },

    async onPdfOnlyLocaleChange() {
      await this.regeneratePdfForLocale();
    },

    addManualEmail() {
      if (this.isValidEmail(this.manualEmail)) {
        if (!this.manualEmails.includes(this.manualEmail)) {
          this.manualEmails.push(this.manualEmail);
        }
        this.manualEmail = "";
      }
    },

    removeManualEmail(index) {
      this.manualEmails.splice(index, 1);
    },

    base64ToBlob(base64, mimeType) {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type: mimeType });
    },

    async loadSendMode() {
      try {
        const res = await wencesApi.get("/api/emails/config/smart-email-enabled");
        this.sendMode = res.data?.data?.sendMode === "mail_client" ? "mail_client" : "server";
      } catch (_) {
        this.sendMode = "server";
      }
    },

    /** Base64 en líneas de 76 caracteres, como pide MIME. */
    mimeBase64Lines(b64) {
      return String(b64 || "").replace(/.{1,76}/g, "$&\r\n");
    },

    utf8ToBase64(text) {
      const bytes = new TextEncoder().encode(String(text || ""));
      let bin = "";
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      return btoa(bin);
    },

    async blobToBase64(blob) {
      const buf = new Uint8Array(await blob.arrayBuffer());
      let bin = "";
      for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]);
      return btoa(bin);
    },

    /**
     * Arma el email como borrador (.eml con X-Unsent): Outlook lo abre como email nuevo,
     * con destinatarios, asunto, texto y adjunto, para mandarlo desde la casilla del usuario.
     */
    async buildDraftEml() {
      const boundary = `----=_ExportAI_${Date.now().toString(36)}`;
      const subject = this.emailSubject || "";
      const html = `<div style="white-space: pre-wrap; line-height: 1.6;">${String(this.emailBody || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>")}</div>`;
      let attachment = null;
      if (this.isExportMode) {
        attachment = {
          name: this.excelFileName,
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          base64: await this.blobToBase64(this.excelBlob),
        };
      } else if (this.effectivePdfBase64 && this.effectivePdfFileName) {
        attachment = { name: this.effectivePdfFileName, type: "application/pdf", base64: this.effectivePdfBase64 };
      }
      const encName = (n) => `=?UTF-8?B?${this.utf8ToBase64(n)}?=`;
      const parts = [
        "X-Unsent: 1",
        `To: ${this.allSelectedRecipients.join(", ")}`,
        `Subject: ${encName(subject)}`,
        "MIME-Version: 1.0",
        `Content-Type: multipart/mixed; boundary="${boundary}"`,
        "",
        `--${boundary}`,
        'Content-Type: text/html; charset="UTF-8"',
        "Content-Transfer-Encoding: base64",
        "",
        this.mimeBase64Lines(this.utf8ToBase64(`<html><body>${html}</body></html>`)),
      ];
      if (attachment) {
        parts.push(
          `--${boundary}`,
          `Content-Type: ${attachment.type}; name="${encName(attachment.name)}"`,
          `Content-Disposition: attachment; filename="${encName(attachment.name)}"`,
          "Content-Transfer-Encoding: base64",
          "",
          this.mimeBase64Lines(attachment.base64)
        );
      }
      parts.push(`--${boundary}--`, "");
      return parts.join("\r\n");
    },

    async downloadDraftEml() {
      const eml = await this.buildDraftEml();
      const base = String(this.emailSubject || this.documentName || "email")
        .replace(/[\\/:*?"<>|]+/g, " ")
        .trim()
        .slice(0, 80) || "email";
      const url = URL.createObjectURL(new Blob([eml], { type: "message/rfc822" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${base}.eml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      await Swal.fire({
        icon: "success",
        title: this.$t("emails.smartEmailDraftDownloaded") || "Draft ready",
        text: this.$t("emails.smartEmailDraftDownloadedText") || "",
        confirmButtonText: this.$t("OK") || "OK",
      });
      this.close();
    },

    async sendEmail() {
      if (this.allSelectedRecipients.length === 0) {
        this.$root.$emit("show-error", "Please select at least one recipient");
        return;
      }

      if (this.sendMode === "mail_client") {
        try {
          this.sending = true;
          await this.downloadDraftEml();
        } catch (error) {
          console.error("Error building email draft:", error);
          this.$root.$emit("show-error", error.message || "Error");
        } finally {
          this.sending = false;
        }
        return;
      }

      try {
        this.sending = true;
        this.$root.$emit("show-loading", this.$t("Sending email...") || "Sending email...");

        let response;

        if (this.isExportMode && this.excelBlob && this.excelFileName) {
          const form = new FormData();
          form.append("modelName", this.modelName);
          form.append("recipients", JSON.stringify(this.allSelectedRecipients));
          form.append("subject", this.emailSubject || "");
          form.append("body", this.emailBody || "");
          form.append("fileName", this.excelFileName);
          form.append("file", this.excelBlob, this.excelFileName);
          response = await wencesApi.post("/api/emails/send-export-email", form, { timeout: 60000 });
        } else if (this.effectivePdfBase64 && this.effectivePdfFileName) {
          // Send PDF as multipart file to avoid JSON body size limit
          const form = new FormData();
          form.append("documentType", this.documentType);
          form.append("documentId", this.documentId);
          form.append("modelName", this.modelName);
          form.append("recipients", JSON.stringify(this.allSelectedRecipients));
          form.append("subject", this.emailSubject || "");
          form.append("body", this.emailBody || "");
          if (this.documentType === "display" && this.viewId) form.append("viewId", this.viewId);
          form.append("pdfFileName", this.effectivePdfFileName);
          const pdfBlob = this.base64ToBlob(this.effectivePdfBase64, "application/pdf");
          form.append("pdf", pdfBlob, this.effectivePdfFileName);

          response = await wencesApi.post("/api/emails/send-template-pdf-upload", form, {
            timeout: 60000,
          });
        } else {
          const payload = {
            documentType: this.documentType,
            documentId: this.documentId,
            modelName: this.modelName,
            recipients: this.allSelectedRecipients,
            subject: this.emailSubject || null,
            body: this.emailBody || null,
          };
          if (this.documentType === "display" && this.viewId) payload.viewId = this.viewId;

          response = await wencesApi.post("/api/emails/send-template-pdf", payload, {
            timeout: 120000,
          });
        }

        if (response.data.success) {
          this.$root.$emit("hide-loading");
          await Swal.fire({
            icon: "success",
            title: this.$tn("Email sent successfully") || "Email sent successfully",
            text: this.$tn("The email has been sent to {count} recipient(s)", {
              count: this.allSelectedRecipients.length,
            }) || `The email has been sent to ${this.allSelectedRecipients.length} recipient(s)`,
            confirmButtonText: this.$tn("OK") || "OK",
          });
          this.close();
        } else {
          this.$root.$emit("hide-loading");
          this.$root.$emit(
            "show-error",
            response.data.message || this.$t("Error sending email") || "Error sending email"
          );
        }
      } catch (error) {
        console.error("Error sending email:", error);
        this.$root.$emit("hide-loading");
        this.$root.$emit(
          "show-error",
          error.response?.data?.message || "Error sending email. Please try again."
        );
      } finally {
        this.sending = false;
      }
    },

    close() {
      this.$emit("close");
    },
  },
};
</script>



